import { AntdTable } from "@/components/common";
import {
	DATE_FORMATS,
	MeditationRetreatStatus,
	TableTitles,
} from "@/constants";
import { Months } from "@/constants/months";
import { useGetMeditationRetreatLevels } from "@/hooks/query-hooks/useLevels";
import { calculateDuration } from "@/libs/utils";
import { Tag } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { RegistrantGenderCountModal } from "../Meditators";
import { getMeditationRetreatsStatus } from "./index";
import { RoomsByMeditationRetreat } from "./RoomsByMeditationRetreat";

interface MeditationRetreatTableProps extends TableProps<MeditationRetreat> {
	dataSource: TableProps<MeditationRetreat>["dataSource"];
	renderColumnKeys: string[];
	TableActionComponent?: React.ComponentType<{ record: MeditationRetreat }>;
	hasFilter?: boolean;
}

export function MeditationRetreatsTable({
	dataSource,
	renderColumnKeys,
	TableActionComponent,
	hasFilter = true,
	...props
}: MeditationRetreatTableProps) {
	const { data: levels } = useGetMeditationRetreatLevels();

	const availableColumns: ColumnsType<MeditationRetreat> = [
		{
			title: TableTitles.retreat_start_month,
			dataIndex: "month",
			key: "month",
			render: (value: string, record) =>
				dayjs(record?.fromDate).format(DATE_FORMATS.MONTH_FORMAT),
			filters: hasFilter
				? Months.map((m) => ({
						text: m,
						value: m,
					}))
				: undefined,
			onFilter: (value, record) =>
				dayjs(record.fromDate).format(DATE_FORMATS.MONTH_FORMAT) ===
				value,
		},
		{
			title: TableTitles.retreat_level,
			dataIndex: ["level", "name"],
			key: "level",
			filters:
				hasFilter && levels !== undefined
					? levels?.map((level) => ({
							text: level.name,
							value: level.name,
						}))
					: undefined,
			onFilter: (value, record) => record.level.name === value,
		},
		{
			title: TableTitles.retreat_name,
			dataIndex: "title",
			key: "title",
		},
		{
			title: TableTitles.retreat_duration,
			dataIndex: "duration",
			key: "duration",
			render: (value: string, record) =>
				calculateDuration(
					dayjs(record.fromDate),
					dayjs(record.toDate),
				) + " days",
		},
		{
			title: TableTitles.retreat_start_date,
			dataIndex: "fromDate",
			key: "fromDate",
			render: (value: string) =>
				dayjs(value).format(DATE_FORMATS.DEFAULT_DISPLAY_DATE),
		},
		{
			title: TableTitles.retreat_end_date,
			dataIndex: "toDate",
			key: "toDate",
			render: (value: string) =>
				dayjs(value).format(DATE_FORMATS.DEFAULT_DISPLAY_DATE),
		},
		{
			title: TableTitles.total_registrants,
			dataIndex: "maxYogiLimit",
			key: "maxYogiLimit",
			render: (value: number, record) => (
				<RegistrantGenderCountModal {...record} />
			),
		},
		{
			title: TableTitles.retreat_status,
			dataIndex: "status",
			key: "status",
			render: (value, record) => {
				const status = getMeditationRetreatsStatus(record);
				return (
					<Tag
						color={
							status === MeditationRetreatStatus.ENDED
								? "red"
								: status === MeditationRetreatStatus.ONGOING
									? "green"
									: "blue"
						}
					>
						{status}
					</Tag>
				);
			},
			filters: hasFilter
				? Object.values(MeditationRetreatStatus).map((status) => ({
						text: status,
						value: status,
					}))
				: undefined,
			onFilter: (value, record) =>
				value === getMeditationRetreatsStatus(record),
		},
		{
			title: TableTitles.retreat_attendance,
			key: "attendance",
			render: (value, record) =>
				`${record?.clockInCount || 0} / ${record?.maxYogiLimit}`,
		},
		{
			title: TableTitles.retreat_rooms,
			dataIndex: "rooms",
			key: "rooms",
			render: (_, record) => (
				<RoomsByMeditationRetreat retreat={record} />
			),
			fixed: "right",
		},
		{
			title: TableTitles.action,
			dataIndex: "action",
			key: "action",
			render: (value: string, record) =>
				TableActionComponent ? (
					<TableActionComponent record={record} />
				) : null,
			fixed: "right",
		},
	];

	const renderColumns = availableColumns.filter((col) =>
		renderColumnKeys?.includes(col.title as string),
	);

	return (
		<>
			<AntdTable
				{...props}
				dataSource={dataSource}
				columns={renderColumns}
				rowKey={(record) => record?.id ?? JSON.stringify(record)}
			/>
		</>
	);
}
